import { z } from 'zod';

export interface JWTSessionDTO {
  accessToken: string;
  refreshToken: string;
  expiresIn: number; // milliseconds related to the access token
  type: string;
}

export interface AccessTokenDTO {
  exp: number;
  iat: number;
  roles: [string];
  sub: string;
}

export const minPasswordLength = 8;

export const LoginRequestSchema = z.object({
  username: z.string(),
  password: z.string(),
});

export const RegisterRequestSchema = z.object({
  email: z
    .string()
    .email({ message: 'Incorrect email format' })
    .refine(
      val => /^[\w.+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(val),
      { message: 'Incorrect email format' },
    ),
  password: z.string().min(minPasswordLength),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  languageCode: z.string(),
});

export const AuthResponseSchema = z.object({
  accessToken: z.string().readonly(),
  expiresIn: z.number().readonly(),
  refreshToken: z.string().readonly(),
  type: z.string().readonly(),
});

export const VerifyEmailRequestSchema = z.object({
  token: z.string(),
  deviceId: z.string().optional(),
});

export const VerifyEmailResponseSchema = z.object({
  redirectUrl: z.string().optional(),
}).passthrough();

export const ResendVerificationRequestSchema = z.object({
  email: z.string().email(),
});

export const ForgotPasswordRequestSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Incorrect email format' }),
});

export const ResetPasswordRequestSchema = z
  .object({
    // TODO: add translation for this texts
    newPassword: z.string().min(minPasswordLength, { message: `Password must contain at least ${minPasswordLength} characters` }),
    confirmPassword: z.string().min(minPasswordLength, { message: `Password must contain at least ${minPasswordLength} characters` }),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'Passwords don\'t match',
    path: ['confirmPassword'],
  });

export const AcceptPoliciesRequestSchema = z.object({
  isPolicyAccepted: z.boolean(),
});

// TypeScript types derived from Zod schemas
export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;
export type AuthResponse = z.infer<typeof AuthResponseSchema>;
export type VerifyEmailRequest = z.infer<typeof VerifyEmailRequestSchema>;
export type VerifyEmailResponse = z.infer<typeof VerifyEmailResponseSchema>;
export type ResendVerificationRequest = z.infer<typeof ResendVerificationRequestSchema>;
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;
export type AcceptPoliciesRequest = z.infer<typeof AcceptPoliciesRequestSchema>;
